import React, { useState, useEffect } from 'react';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs, setDoc, addDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

const LICENSED_TRADES = ['Electrical', 'Plumbing', 'HVAC', 'Roofing'];

const REQUIRED_WORKER_AGREEMENTS = [
  'independent_contractor', 'marketplace_only', 'taxes',
  'tools', 'work_authorized', 'liability',
];

const ISSUE_REASONS = [
  'Unsafe working conditions',
  'Hostile or threatening behavior',
  'Non-payment or payment dispute',
  'Job description does not match reality',
  'Harassment or discrimination',
  'Other serious concern',
];

const getTradeStatus = (tradeProfile) => {
  const requiresLicense = LICENSED_TRADES.includes(tradeProfile.trade);
  if (!requiresLicense || tradeProfile.level !== 'Master') {
    return { status: 'active', label: '✅ Active', color: '#155724', bg: '#d4edda' };
  }
  if (tradeProfile.licenseStatus === 'verified') {
    return { status: 'active_licensed', label: '✅ Active — Licensed', color: '#155724', bg: '#d4edda' };
  }
  if (tradeProfile.licenseStatus === 'pending') {
    return { status: 'pending', label: '⏳ License Pending', color: '#856404', bg: '#fff3cd' };
  }
  return { status: 'unlicensed', label: '⚠️ Not in Licensed Searches', color: '#856404', bg: '#fff8e1' };
};

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(true);
  const [todayJobs, setTodayJobs] = useState([]);
  const [confirmations, setConfirmations] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [workerProfile, setWorkerProfile] = useState(null);
  const [expandedConfirm, setExpandedConfirm] = useState({});
  const [pendingHours, setPendingHours] = useState({});
  const [pendingNotes, setPendingNotes] = useState({});
  const [pendingRequestCount, setPendingRequestCount] = useState(0);

  const [showEscalation, setShowEscalation] = useState({});
  const [escalationDesc, setEscalationDesc] = useState({});
  const [escalationDoc, setEscalationDoc] = useState({});
  const [submittingEscalation, setSubmittingEscalation] = useState(false);

  const [issueStep, setIssueStep] = useState({});
  const [issueReason, setIssueReason] = useState({});
  const [issueDescription, setIssueDescription] = useState({});
  const [issueTerminate, setIssueTerminate] = useState({});
  const [submittingIssue, setSubmittingIssue] = useState(false);

  const todayStr = new Date().toISOString().split('T')[0];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (!u) setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    const loadUser = async () => {
      try {
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const type = docSnap.data().userType;
          setUserType(type);
          setUserName(docSnap.data().name || '');
          await loadTodayJobs(type);
          if (type === 'worker') {
            const profileSnap = await getDoc(doc(db, 'workerProfiles', user.uid));
            if (profileSnap.exists()) setWorkerProfile(profileSnap.data());
            await loadPendingCount();
          }
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    loadUser();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPendingCount = async () => {
    if (!user) return;
    try {
      const q = query(
        collection(db, 'jobRequests'),
        where('workerId', '==', user.uid),
        where('status', '==', 'pending')
      );
      const snap = await getDocs(q);
      setPendingRequestCount(snap.size);
    } catch (err) { console.error(err); }
  };

  const loadTodayJobs = async (type) => {
    if (!user) return;
    try {
      const field = type === 'contractor' ? 'contractorId' : 'workerId';
      const q = query(
        collection(db, 'jobRequests'),
        where(field, '==', user.uid),
        where('status', '==', 'accepted')
      );
      const snapshot = await getDocs(q);
      const jobs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const activeToday = jobs.filter(job => {
        if (!job.jobDate || !job.jobEndDate) return false;
        const start = new Date(job.jobDate + 'T00:00:00');
        const end = new Date(job.jobEndDate + 'T00:00:00');
        if (today < start || today > end) return false;
        const dayOfWeek = today.getDay();
        if (dayOfWeek === 6 && !job.includeSaturdays) return false;
        if (dayOfWeek === 0 && !job.includeSundays) return false;
        return true;
      });
      setTodayJobs(activeToday);

      const confMap = {};
      const now = new Date();
      for (const job of activeToday) {
        const confId = `${job.id}_${todayStr}`;
        const confRef = doc(db, 'jobConfirmations', confId);
        const confDoc = await getDoc(confRef);
        if (confDoc.exists()) {
          let confData = confDoc.data();
          if (
            confData.workerConfirmed &&
            !confData.contractorConfirmed &&
            confData.status === 'pending' &&
            confData.workerConfirmedAt
          ) {
            const wAt = confData.workerConfirmedAt?.toDate
              ? confData.workerConfirmedAt.toDate()
              : new Date(confData.workerConfirmedAt);
            const hoursSince = (now - wAt) / (1000 * 60 * 60);
            if (hoursSince >= 24) {
              const autoData = {
                ...confData,
                contractorConfirmed: true,
                contractorHours: confData.workerHours,
                contractorNotes: 'Auto-confirmed — contractor did not respond within 24 hours',
                contractorConfirmedAt: now,
                status: 'verified',
                confirmedHours: confData.workerHours,
                autoVerified: true,
                autoVerifiedAt: now,
              };
              await setDoc(confRef, autoData, { merge: true });
              confData = autoData;
            }
          }
          confMap[job.id] = confData;
        }
      }
      setConfirmations(confMap);
    } catch (err) { console.error(err); }
  };

  const sendSystemMessage = async (job, text) => {
    try {
      const convsQuery = query(
        collection(db, 'conversations'),
        where('jobRequestId', '==', job.id)
      );
      const convsSnap = await getDocs(convsQuery);
      let convId;
      if (!convsSnap.empty) {
        convId = convsSnap.docs[0].id;
      } else {
        const newConv = await addDoc(collection(db, 'conversations'), {
          jobRequestId: job.id,
          workerId: job.workerId,
          contractorId: job.contractorId,
          workerName: job.workerName,
          createdAt: new Date(),
          lastMessage: text,
          lastMessageAt: new Date(),
        });
        convId = newConv.id;
      }
      await addDoc(collection(db, 'conversations', convId, 'messages'), {
        text, senderId: 'system', senderName: 'TradePower',
        system: true, createdAt: new Date(), jobId: job.id,
      });
    } catch (err) { console.error('System message error:', err); }
  };

  const handleConfirm = async (job, hours, notes) => {
    if (!hours) { alert('Please select hours worked today.'); return; }
    setConfirming(true);
    try {
      const confId = `${job.id}_${todayStr}`;
      const confRef = doc(db, 'jobConfirmations', confId);
      const existing = confirmations[job.id] || {};
      const isWorker = userType === 'worker';
      const now = new Date();

      const workerConfirmed = isWorker ? true : (existing.workerConfirmed || false);
      const contractorConfirmed = !isWorker ? true : (existing.contractorConfirmed || false);
      const workerHours = isWorker ? hours : (existing.workerHours || null);
      const contractorHours = !isWorker ? hours : (existing.contractorHours || null);
      const workerNotes = isWorker ? notes : (existing.workerNotes || '');
      const contractorNotes = !isWorker ? notes : (existing.contractorNotes || '');
      const workerConfirmedAt = isWorker ? now : (existing.workerConfirmedAt || null);
      const contractorConfirmedAt = !isWorker ? now : (existing.contractorConfirmedAt || null);

      let status = 'pending';
      let confirmedHours = null;
      if (workerConfirmed && contractorConfirmed) {
        if (workerHours === contractorHours) {
          status = 'verified';
          confirmedHours = workerHours;
        } else {
          status = 'disputed';
        }
      }

      const updateData = {
        jobRequestId: job.id, date: todayStr,
        workerId: job.workerId, contractorId: job.contractorId,
        workerName: job.workerName, workerRate: job.workerRate,
        contractorRate: job.contractorRate,
        jobDescription: job.jobDescription, jobAddress: job.jobAddress,
        workerConfirmed, contractorConfirmed,
        workerHours, contractorHours, workerNotes, contractorNotes,
        workerConfirmedAt, contractorConfirmedAt,
        status, confirmedHours, autoVerified: false,
        createdAt: existing.createdAt || now,
      };

      await setDoc(confRef, updateData, { merge: true });
      setConfirmations(prev => ({ ...prev, [job.id]: updateData }));
      setExpandedConfirm(prev => ({ ...prev, [job.id]: false }));

      if (status === 'disputed') {
        const disputeMsg =
          `⚠️ HOURS DISPUTE — ${todayStr}\n\n` +
          `Worker reported: ${workerHours} hours${workerNotes ? ` ("${workerNotes}")` : ''}\n` +
          `Contractor confirmed: ${contractorHours} hours${contractorNotes ? ` ("${contractorNotes}")` : ''}\n\n` +
          `This dispute has been flagged for TradePower admin review.\n\n` +
          `This is an automated system notice. You cannot reply to this message.`;
        await sendSystemMessage(job, disputeMsg);
      }
    } catch (err) { console.error(err); }
    setConfirming(false);
  };

  const handleEscalationDocUpload = (e, jobId) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('File too large. Max 5MB.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setEscalationDoc(prev => ({ ...prev, [jobId]: { name: file.name, data: ev.target.result } }));
    reader.readAsDataURL(file);
  };

  const handleSubmitEscalation = async (job, conf) => {
    const desc = escalationDesc[job.id];
    if (!desc?.trim()) { alert('Please describe the disagreement before escalating.'); return; }
    setSubmittingEscalation(true);
    try {
      await addDoc(collection(db, 'disputes'), {
        jobId: job.id, confId: `${job.id}_${conf.date}`, date: conf.date,
        workerId: job.workerId, workerName: job.workerName,
        contractorId: job.contractorId,
        workerHours: conf.workerHours, contractorHours: conf.contractorHours,
        escalationDescription: desc,
        hasEvidence: !!escalationDoc[job.id],
        evidenceFileName: escalationDoc[job.id]?.name || null,
        submittedBy: user.uid, submittedAt: new Date(), status: 'escalated',
      });
      const subject = encodeURIComponent(`TradePower Dispute Escalation — Job ID: ${job.id}`);
      const body = encodeURIComponent(
        `JOB ID: ${job.id}\nDATE: ${conf.date}\nWORKER: ${job.workerName}\n` +
        `JOB: ${job.jobDescription}\nWORKER HOURS CLAIMED: ${conf.workerHours}\n` +
        `CONTRACTOR HOURS CONFIRMED: ${conf.contractorHours}\n\n` +
        `DESCRIPTION OF DISAGREEMENT:\n${desc}\n\n` +
        (escalationDoc[job.id] ? `EVIDENCE: I have attached evidence named "${escalationDoc[job.id].name}".\n\n` : '') +
        `Please review and resolve this dispute. I understand this may take 1–2 weeks.`
      );
      window.open(`mailto:support@tradepowerapp.com?subject=${subject}&body=${body}`);
      await sendSystemMessage(job,
        `📬 DISPUTE ESCALATED — ${conf.date}\n\nA party has escalated this hours dispute to TradePower support.\n\nResolution timeline: 1–2 weeks.\n\nThis is an automated system notice.`
      );
      setShowEscalation(prev => ({ ...prev, [job.id]: false }));
      setEscalationDesc(prev => ({ ...prev, [job.id]: '' }));
      setEscalationDoc(prev => ({ ...prev, [job.id]: null }));
      alert('Escalation submitted. Your email client should have opened with a pre-filled message to support@tradepowerapp.com. Please send it to complete the escalation.');
    } catch (err) { console.error(err); }
    setSubmittingEscalation(false);
  };

  const handleSubmitIssue = async (job) => {
    const reason = issueReason[job.id];
    const description = issueDescription[job.id];
    const terminate = issueTerminate[job.id];
    if (!reason) { alert('Please select a reason.'); return; }
    if (!description?.trim()) { alert('Please describe the issue.'); return; }
    setSubmittingIssue(true);
    try {
      await addDoc(collection(db, 'jobIssues'), {
        jobId: job.id, reportedBy: user.uid, reportedByType: userType,
        workerId: job.workerId, contractorId: job.contractorId,
        workerName: job.workerName, reason, description,
        requestedTermination: terminate || false, status: 'open', createdAt: new Date(),
      });
      const sysMsg = terminate
        ? `🚨 JOB TERMINATION REQUESTED — ${todayStr}\n\nA ${userType} has requested early termination.\n\nReason: ${reason}\n\nThe other party has 24 hours to respond.\n\nThis is an automated system notice.`
        : `⚠️ JOB CONCERN FLAGGED — ${todayStr}\n\nA ${userType} has flagged a concern.\n\nReason: ${reason}\n\nThis is an automated system notice.`;
      await sendSystemMessage(job, sysMsg);
      setIssueStep(prev => ({ ...prev, [job.id]: 0 }));
      setIssueReason(prev => ({ ...prev, [job.id]: '' }));
      setIssueDescription(prev => ({ ...prev, [job.id]: '' }));
      setIssueTerminate(prev => ({ ...prev, [job.id]: false }));
      alert(terminate
        ? 'Termination request submitted. The other party has been notified.'
        : 'Concern flagged. Admin has been notified.'
      );
    } catch (err) { console.error(err); }
    setSubmittingIssue(false);
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  const hasConfirmedToday = (jobId) => {
    const conf = confirmations[jobId];
    if (!conf) return false;
    return userType === 'worker' ? conf.workerConfirmed : conf.contractorConfirmed;
  };

  const isVerifiedToday = (jobId) => confirmations[jobId]?.status === 'verified';
  const isDisputedToday = (jobId) => confirmations[jobId]?.status === 'disputed';

  const getAutoVerifyCountdown = (conf) => {
    if (!conf?.workerConfirmed || conf?.contractorConfirmed) return null;
    const confirmedAt = conf.workerConfirmedAt?.toDate
      ? conf.workerConfirmedAt.toDate()
      : conf.workerConfirmedAt instanceof Date
        ? conf.workerConfirmedAt
        : new Date(conf.workerConfirmedAt);
    if (!confirmedAt || isNaN(confirmedAt)) return null;
    const autoAt = new Date(confirmedAt.getTime() + 24 * 60 * 60 * 1000);
    const msLeft = autoAt - new Date();
    if (msLeft <= 0) return 'Auto-verifying soon...';
    const hLeft = Math.floor(msLeft / (1000 * 60 * 60));
    const mLeft = Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60));
    if (hLeft > 0) return `Auto-confirms in ${hLeft}h ${mLeft}m if contractor doesn't respond`;
    return `Auto-confirms in ${mLeft} minutes if contractor doesn't respond`;
  };

  const getWorkerActiveStatus = () => {
    if (!workerProfile) return null;
    const hasName = !!workerProfile.name;
    const hasRate = !!workerProfile.hourlyRate;
    const hasTrades = workerProfile.tradeProfiles?.length > 0;
    const hasAgreements = REQUIRED_WORKER_AGREEMENTS.every(id => workerProfile.agreements?.[id] === true);
    if (!hasName || !hasRate || !hasTrades || !hasAgreements) {
      return { status: 'inactive', label: '⚠️ Inactive', color: '#856404', bg: '#fff3cd',
        missing: { hasName, hasRate, hasTrades, hasAgreements } };
    }
    const hasPending = workerProfile.tradeProfiles?.some(t =>
      LICENSED_TRADES.includes(t.trade) && t.level === 'Master' && t.licenseStatus === 'pending'
    );
    const hasUnlicensed = workerProfile.tradeProfiles?.some(t =>
      LICENSED_TRADES.includes(t.trade) && t.level === 'Master' && t.licenseStatus === 'none'
    );
    if (hasPending) return { status: 'pending', label: '🟢 Active — License Pending Review', color: '#155724', bg: '#d4edda', note: 'pending' };
    if (hasUnlicensed) return { status: 'unlicensed', label: '🟢 Active', color: '#155724', bg: '#d4edda', note: 'unlicensed' };
    return { status: 'active', label: '🟢 Active', color: '#155724', bg: '#d4edda' };
  };

  if (loading) return <div style={styles.loadingContainer}><p>Loading...</p></div>;

  const activeStatus = getWorkerActiveStatus();

  const renderConfirmCard = (job) => {
    const confirmed = hasConfirmedToday(job.id);
    const verified = isVerifiedToday(job.id);
    const disputed = isDisputedToday(job.id);
    const conf = confirmations[job.id] || {};
    const isWorker = userType === 'worker';
    const myHours = isWorker ? conf.workerHours : conf.contractorHours;
    const isExpanded = expandedConfirm[job.id];
    const defaultHours = parseInt(job.jobDuration) || 8;
    const selectedHours = pendingHours[job.id] !== undefined ? pendingHours[job.id] : defaultHours;
    const currentIssueStep = issueStep[job.id] || 0;

    return (
      <div key={job.id} style={styles.confirmCard}>
        <div style={styles.confirmCardTop}>
          <div style={styles.confirmCardLeft}>
            <p style={styles.confirmJobTitle}>{isWorker ? '🏗️ Job' : `👷 ${job.workerName}`}</p>
            <p style={styles.confirmJobDesc}>{job.jobDescription}</p>
            <p style={styles.confirmJobAddr}>📍 {job.jobAddress}</p>
          </div>
          {verified && (
            <div style={styles.verifiedHoursBox}>
              <span style={styles.verifiedHoursIcon}>✅</span>
              <span style={styles.verifiedHoursText}>{conf.confirmedHours}h verified</span>
              {conf.autoVerified && <span style={styles.autoVerifiedTag}>Auto-confirmed</span>}
              <span style={styles.verifiedHoursAmount}>${Math.round((job.contractorRate || 0) * (conf.confirmedHours || 0))}</span>
            </div>
          )}
          {disputed && (
            <div style={styles.disputedBox}>
              <span style={styles.disputedIcon}>⚠️</span>
              <span style={styles.disputedText}>Disputed</span>
            </div>
          )}
        </div>

        <div style={styles.confirmStatusRow}>
          <div style={styles.confirmPartyStatus}>
            <span style={styles.confirmPartyLabel}>👷 Worker</span>
            {conf.workerConfirmed
              ? <span style={styles.confirmedHoursBadge}>{conf.workerHours}h confirmed</span>
              : <span style={styles.awaitingSmallBadge}>Pending</span>}
          </div>
          <div style={styles.confirmDivider}>|</div>
          <div style={styles.confirmPartyStatus}>
            <span style={styles.confirmPartyLabel}>🏗️ Contractor</span>
            {conf.contractorConfirmed
              ? <span style={conf.autoVerified ? styles.autoConfirmedHoursBadge : styles.confirmedHoursBadge}>
                  {conf.contractorHours}h {conf.autoVerified ? '(auto)' : 'confirmed'}
                </span>
              : <span style={styles.awaitingSmallBadge}>Pending</span>}
          </div>
          {verified && <><div style={styles.confirmDivider}>|</div><span style={styles.verifiedBadgeSmall}>✅ Verified</span></>}
          {disputed && <><div style={styles.confirmDivider}>|</div><span style={styles.disputedBadgeSmall}>⚠️ Under Review</span></>}
        </div>

        {!confirmed && conf.workerConfirmed && !isWorker && (
          <div style={styles.workerClaimedBox}>
            <span style={styles.workerClaimedText}>
              👷 Worker confirmed <strong>{conf.workerHours} hours</strong> today
              {conf.workerNotes && <span style={styles.workerClaimedNote}> — "{conf.workerNotes}"</span>}
            </span>
          </div>
        )}

        {isWorker && confirmed && !verified && !disputed && getAutoVerifyCountdown(conf) && (
          <div style={styles.autoVerifyCountdown}>
            <span style={styles.autoVerifyText}>⏱️ {getAutoVerifyCountdown(conf)}</span>
          </div>
        )}

        {!confirmed && !verified && !disputed && (
          <>
            {!isExpanded ? (
              <button style={styles.confirmBtn} onClick={() => {
                setExpandedConfirm(prev => ({ ...prev, [job.id]: true }));
                setPendingHours(prev => ({ ...prev, [job.id]: defaultHours }));
              }}>
                {isWorker ? '✅ I Worked Today' : '✅ Confirm Worker Hours'}
              </button>
            ) : (
              <div style={styles.hourSelectorBox}>
                <p style={styles.hourSelectorTitle}>
                  {isWorker ? 'How many hours did you work today?' : `Confirm hours worked by ${job.workerName?.split(' ')[0]}:`}
                </p>
                {!isWorker && conf.workerHours && (
                  <p style={styles.workerClaimedSmall}>Worker claimed: <strong>{conf.workerHours} hours</strong></p>
                )}
                <div style={styles.hourButtons}>
                  {[4, 5, 6, 7, 8, 9, 10, 11, 12].map(h => (
                    <button key={h}
                      style={selectedHours === h ? styles.hourBtnActive : styles.hourBtn}
                      onClick={() => setPendingHours(prev => ({ ...prev, [job.id]: h }))}>
                      {h}h
                    </button>
                  ))}
                </div>
                <input style={styles.notesInput} type="text"
                  placeholder="Notes (optional)"
                  value={pendingNotes[job.id] || ''}
                  onChange={e => setPendingNotes(prev => ({ ...prev, [job.id]: e.target.value }))} />
                {isWorker && (
                  <div style={styles.autoVerifyNote}>
                    ℹ️ If the contractor doesn't confirm within 24 hours, your hours will be auto-approved.
                  </div>
                )}
                <div style={styles.hourSelectorButtons}>
                  <button style={styles.cancelHoursBtn}
                    onClick={() => setExpandedConfirm(prev => ({ ...prev, [job.id]: false }))}>Cancel</button>
                  <button
                    style={confirming ? styles.submitHoursBtnDisabled : styles.submitHoursBtn}
                    onClick={() => handleConfirm(job, selectedHours, pendingNotes[job.id] || '')}
                    disabled={confirming}>
                    {confirming ? 'Submitting...' : `Submit — ${selectedHours} Hours`}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {confirmed && !verified && !disputed && (
          <div style={styles.awaitingBox}>
            <p style={styles.awaitingText}>
              ⏳ You confirmed <strong>{myHours} hours</strong> — waiting for {isWorker ? 'contractor' : 'worker'} to confirm
            </p>
          </div>
        )}

        {disputed && (
          <div style={styles.disputeDetailBox}>
            <p style={styles.disputeDetailText}>👷 Worker reported <strong>{conf.workerHours} hours</strong>{conf.workerNotes && ` — "${conf.workerNotes}"`}</p>
            <p style={styles.disputeDetailText}>🏗️ Contractor confirmed <strong>{conf.contractorHours} hours</strong>{conf.contractorNotes && ` — "${conf.contractorNotes}"`}</p>
            <p style={styles.disputeNote}>TradePower admin will review and resolve within 1 business day.</p>
            {!showEscalation[job.id] ? (
              <button style={styles.escalateLink} onClick={() => setShowEscalation(prev => ({ ...prev, [job.id]: true }))}>
                Disagree with the resolution? Escalate to TradePower Support →
              </button>
            ) : (
              <div style={styles.escalationForm}>
                <p style={styles.escalationTitle}>📬 Escalate to TradePower Support</p>
                <p style={styles.escalationWarning}>⚠️ Escalated disputes may take 1–2 weeks to resolve.</p>
                <label style={styles.escalationLabel}>Describe your disagreement *</label>
                <textarea style={styles.escalationTextarea} placeholder="Explain why you disagree..."
                  value={escalationDesc[job.id] || ''}
                  onChange={e => setEscalationDesc(prev => ({ ...prev, [job.id]: e.target.value }))}
                  rows={4} />
                <label style={styles.escalationUploadBtn}>
                  📎 {escalationDoc[job.id] ? `✅ ${escalationDoc[job.id].name}` : 'Attach Evidence File'}
                  <input type="file" accept="image/*,.pdf,.doc,.docx"
                    onChange={e => handleEscalationDocUpload(e, job.id)} style={{ display: 'none' }} />
                </label>
                <div style={styles.escalationButtons}>
                  <button style={styles.cancelEscalationBtn}
                    onClick={() => setShowEscalation(prev => ({ ...prev, [job.id]: false }))}>Cancel</button>
                  <button
                    style={submittingEscalation ? styles.submitEscalationBtnDisabled : styles.submitEscalationBtn}
                    disabled={submittingEscalation}
                    onClick={() => handleSubmitEscalation(job, conf)}>
                    {submittingEscalation ? 'Submitting...' : '📬 Submit Escalation'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {verified && conf.autoVerified && (
          <div style={styles.autoVerifiedNotice}>
            <p style={styles.autoVerifiedNoticeText}>ℹ️ Auto-confirmed — contractor did not respond within 24 hours.</p>
          </div>
        )}

        {!verified && currentIssueStep === 0 && (
          <button style={styles.reportIssueLink}
            onClick={() => setIssueStep(prev => ({ ...prev, [job.id]: 1 }))}>
            Having a serious issue with this job?
          </button>
        )}

        {currentIssueStep === 1 && (
          <div style={styles.issueBox}>
            <p style={styles.issueWarningTitle}>⚠️ Before you continue</p>
            <p style={styles.issueWarningText}>
              This section is for serious concerns only — unsafe conditions, harassment, or situations you cannot continue working through.
            </p>
            <div style={styles.issueStepButtons}>
              <button style={styles.issueCancelBtn} onClick={() => setIssueStep(prev => ({ ...prev, [job.id]: 0 }))}>Go Back</button>
              <button style={styles.issueContinueBtn} onClick={() => setIssueStep(prev => ({ ...prev, [job.id]: 2 }))}>This is a serious concern →</button>
            </div>
          </div>
        )}

        {currentIssueStep === 2 && (
          <div style={styles.issueBox}>
            <p style={styles.issueStepTitle}>What is the nature of the concern?</p>
            <div style={styles.issueReasonList}>
              {ISSUE_REASONS.map(r => (
                <button key={r}
                  style={issueReason[job.id] === r ? styles.issueReasonActive : styles.issueReason}
                  onClick={() => setIssueReason(prev => ({ ...prev, [job.id]: r }))}>
                  {r}
                </button>
              ))}
            </div>
            <textarea style={styles.issueTextarea}
              placeholder="Describe the specific situation in detail..."
              value={issueDescription[job.id] || ''}
              onChange={e => setIssueDescription(prev => ({ ...prev, [job.id]: e.target.value }))}
              rows={3} />
            <div style={styles.issueStepButtons}>
              <button style={styles.issueCancelBtn} onClick={() => setIssueStep(prev => ({ ...prev, [job.id]: 1 }))}>Back</button>
              <button style={styles.issueContinueBtn} onClick={() => setIssueStep(prev => ({ ...prev, [job.id]: 3 }))}>Continue →</button>
            </div>
          </div>
        )}

        {currentIssueStep === 3 && (
          <div style={styles.issueBox}>
            <p style={styles.issueStepTitle}>What outcome do you need?</p>
            <div style={styles.issueOutcomeRow}>
              <div style={!issueTerminate[job.id] ? styles.issueOutcomeSelected : styles.issueOutcome}
                onClick={() => setIssueTerminate(prev => ({ ...prev, [job.id]: false }))}>
                <p style={styles.issueOutcomeTitle}>🚩 Flag for Review</p>
                <p style={styles.issueOutcomeDesc}>Job stays active. Admin is notified.</p>
              </div>
              <div style={issueTerminate[job.id] ? styles.issueOutcomeSelected : styles.issueOutcome}
                onClick={() => setIssueTerminate(prev => ({ ...prev, [job.id]: true }))}>
                <p style={styles.issueOutcomeTitle}>🛑 Request Termination</p>
                <p style={styles.issueOutcomeDesc}>Other party notified. 24 hours to respond.</p>
              </div>
            </div>
            <div style={styles.issueStepButtons}>
              <button style={styles.issueCancelBtn} onClick={() => setIssueStep(prev => ({ ...prev, [job.id]: 2 }))}>Back</button>
              <button
                style={submittingIssue ? styles.issueSubmitBtnDisabled : (issueTerminate[job.id] ? styles.issueSubmitBtnRed : styles.issueSubmitBtn)}
                disabled={submittingIssue}
                onClick={() => handleSubmitIssue(job)}>
                {submittingIssue ? 'Submitting...' : issueTerminate[job.id] ? '🛑 Submit Termination Request' : '🚩 Submit Concern'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.navbar}>
        <h1 style={{ ...styles.logo, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>TradePower</h1>
        <div style={styles.navRight}>
          <span style={styles.userTypeBadge}>{userType === 'contractor' ? '🏗️ Contractor' : '👷 Worker'}</span>
          <button style={styles.logoutButton} onClick={handleLogout}>Log Out</button>
        </div>
      </div>

      <div style={styles.content}>
        {todayJobs.length > 0 && (
          <div style={styles.confirmSection}>
            <h3 style={styles.confirmSectionTitle}>📋 Today's Job Check-In</h3>
            <p style={styles.confirmSectionSubtitle}>
              {userType === 'worker'
                ? "Confirm your hours worked today — auto-approves in 24hrs if contractor doesn't respond"
                : 'Review and confirm hours for each active worker today'}
            </p>
            {todayJobs.map(job => renderConfirmCard(job))}
          </div>
        )}

        {userType === 'worker' && (
          <div style={activeStatus?.status === 'inactive' ? styles.inactiveStatusCard : styles.activeStatusCard}>
            <div style={styles.activeStatusHeader}>
              <div>
                <h3 style={styles.activeStatusTitle}>Profile Status</h3>
                <p style={styles.activeStatusSubtitle}>What contractors can currently see</p>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {activeStatus && (
                  <span style={{ ...styles.activeStatusBadge, backgroundColor: activeStatus.bg, color: activeStatus.color }}>
                    {activeStatus.label}
                  </span>
                )}
                <button style={styles.editProfileBtn} onClick={() => navigate('/worker-profile')}>✏️ Edit Profile</button>
              </div>
            </div>

            {(!workerProfile || activeStatus?.status === 'inactive') ? (
              <div style={styles.inactiveBox}>
                <p style={styles.inactiveText}>Your profile is incomplete. Complete all required steps to become active and visible to contractors.</p>
                <div style={styles.inactiveChecklist}>
                  <div style={activeStatus?.missing?.hasName ? styles.checkItemDone : styles.checkItemMissing}>{activeStatus?.missing?.hasName ? '✅' : '○'} Full name added</div>
                  <div style={activeStatus?.missing?.hasRate ? styles.checkItemDone : styles.checkItemMissing}>{activeStatus?.missing?.hasRate ? '✅' : '○'} Hourly rate set</div>
                  <div style={activeStatus?.missing?.hasTrades ? styles.checkItemDone : styles.checkItemMissing}>{activeStatus?.missing?.hasTrades ? '✅' : '○'} At least one trade profile added</div>
                  <div style={activeStatus?.missing?.hasAgreements ? styles.checkItemDone : styles.checkItemMissingClickable}>
                    {activeStatus?.missing?.hasAgreements ? '✅' : '○'} Platform agreements acknowledged
                    {!activeStatus?.missing?.hasAgreements && <span style={styles.checkItemLink}> → Complete in Profile</span>}
                  </div>
                </div>
                <div style={styles.inactiveActions}>
                  <button style={styles.completeProfileBtn} onClick={() => navigate('/worker-profile')}>Complete Profile →</button>
                </div>
              </div>
            ) : (
              <>
                <div style={styles.tradeStatusGrid}>
                  {workerProfile.tradeProfiles?.map(tp => {
                    const ts = getTradeStatus(tp);
                    return (
                      <div key={tp.id} style={styles.tradeStatusItem}>
                        <div style={styles.tradeStatusLeft}>
                          <span style={styles.tradeStatusName}>{tp.trade}</span>
                          <span style={styles.tradeStatusLevel}>{tp.level} • {tp.yearsExperience} yrs</span>
                          {tp.industries?.length > 0 && <span style={styles.tradeStatusIndustries}>{tp.industries.join(' • ')}</span>}
                        </div>
                        <span style={{ ...styles.tradeStatusBadge, backgroundColor: ts.bg, color: ts.color }}>{ts.label}</span>
                      </div>
                    );
                  })}
                </div>
                {activeStatus?.note === 'pending' && <div style={styles.statusNote}>⏳ One or more contractor licenses are pending admin review (1–2 business days).</div>}
                {activeStatus?.note === 'unlicensed' && <div style={styles.statusNoteWarn}>⚠️ Upload your contractor license to appear in "Licensed Contractor" searches.</div>}
                {workerProfile.certifications?.length > 0 && (
                  <div style={styles.certStatusRow}>
                    <span style={styles.certStatusLabel}>🏆 Certifications:</span>
                    <span style={styles.certStatusValue}>{workerProfile.certifications.join(' • ')}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <h2 style={styles.welcome}>Welcome back{userName ? `, ${userName.split(' ')[0]}` : ''}! 👋</h2>
        <p style={styles.subtitle}>
          {userType === 'contractor' ? 'Find reliable skilled workers for your projects' : 'Manage your profile and availability'}
        </p>

        {userType === 'contractor' && (
          <div style={styles.cards}>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>👷 Find Workers</h3>
              <p style={styles.cardText}>Browse licensed tradespeople available in your area</p>
              <button style={styles.cardButton} onClick={() => navigate('/browse')}>Browse Workers</button>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>📋 My Requests</h3>
              <p style={styles.cardText}>Track pending, accepted and declined job requests</p>
              <button style={styles.cardButton} onClick={() => navigate('/job-history')}>View Requests</button>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>💬 Messages</h3>
              <p style={styles.cardText}>View and respond to job conversations</p>
              <button style={styles.cardButton} onClick={() => navigate('/messages')}>Open Messages</button>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>📅 My Calendar</h3>
              <p style={styles.cardText}>View all your upcoming booked workers by date</p>
              <button style={styles.cardButton} onClick={() => navigate('/calendar')}>View Calendar</button>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>⚙️ Account Settings</h3>
              <p style={styles.cardText}>Manage your company profile and billing</p>
              <button style={styles.cardButton} onClick={() => navigate('/contractor-profile')}>Edit Account</button>
            </div>
          </div>
        )}

        {userType === 'worker' && (
          <div style={styles.cards}>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>👤 My Profile</h3>
              <p style={styles.cardText}>Set up your profile, trades, and availability</p>
              <button style={styles.cardButton} onClick={() => navigate('/worker-profile')}>Edit Profile</button>
            </div>
            <div style={{ ...styles.card, position: 'relative' }}>
              {pendingRequestCount > 0 && (
                <div style={styles.notificationBadge}>{pendingRequestCount}</div>
              )}
              <h3 style={styles.cardTitle}>📬 Job Requests</h3>
              <p style={styles.cardText}>View and respond to incoming job requests</p>
              <button style={styles.cardButton} onClick={() => navigate('/job-requests')}>View Requests</button>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>📆 My Availability</h3>
              <p style={styles.cardText}>Block out dates and days you're not available</p>
              <button style={styles.cardButton} onClick={() => navigate('/availability')}>Set Availability</button>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>💬 Messages</h3>
              <p style={styles.cardText}>Chat with contractors about your jobs</p>
              <button style={styles.cardButton} onClick={() => navigate('/messages')}>Open Messages</button>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>📅 My Calendar</h3>
              <p style={styles.cardText}>View all your upcoming accepted jobs by date</p>
              <button style={styles.cardButton} onClick={() => navigate('/calendar')}>View Calendar</button>
            </div>
          </div>
        )}

        {!userType && (
          <div style={styles.noTypeContainer}>
            <p>We couldn't determine your account type. Please log out and register again.</p>
            <button style={styles.cardButton} onClick={handleLogout}>Log Out</button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5', display: 'flex', flexDirection: 'column' },
  loadingContainer: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  navbar: {
    backgroundColor: 'white', padding: '16px 24px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  logo: { fontSize: '24px', fontWeight: 'bold', color: '#E94560', margin: 0 },
  navRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  userTypeBadge: { padding: '6px 12px', backgroundColor: '#f0f2f5', borderRadius: '20px', fontSize: '13px', color: '#444' },
  logoutButton: {
    padding: '8px 16px', backgroundColor: 'transparent',
    color: '#E94560', border: '1px solid #E94560', borderRadius: '8px', cursor: 'pointer', fontSize: '14px',
  },
  content: { padding: '24px 16px', maxWidth: '1100px', margin: '0 auto', width: '100%', flex: 1, boxSizing: 'border-box' },
  confirmSection: {
    backgroundColor: 'white', borderRadius: '12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '20px',
    marginBottom: '20px', border: '2px solid #E94560',
  },
  confirmSectionTitle: { fontSize: '18px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  confirmSectionSubtitle: { fontSize: '13px', color: '#666', marginBottom: '16px' },
  confirmCard: {
    backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '16px',
    marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '12px',
  },
  confirmCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' },
  confirmCardLeft: { flex: 1 },
  confirmJobTitle: { fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  confirmJobDesc: { fontSize: '13px', color: '#555', marginBottom: '4px' },
  confirmJobAddr: { fontSize: '12px', color: '#999' },
  verifiedHoursBox: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    backgroundColor: '#d4edda', borderRadius: '10px', padding: '12px 16px',
    flexShrink: 0, minWidth: '90px', gap: '2px',
  },
  verifiedHoursIcon: { fontSize: '20px' },
  verifiedHoursText: { fontSize: '13px', fontWeight: 'bold', color: '#155724' },
  verifiedHoursAmount: { fontSize: '16px', fontWeight: 'bold', color: '#155724' },
  autoVerifiedTag: { fontSize: '10px', color: '#6c757d', backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '6px' },
  disputedBox: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    backgroundColor: '#fff3cd', borderRadius: '10px', padding: '12px 16px', flexShrink: 0, gap: '2px',
  },
  disputedIcon: { fontSize: '20px' },
  disputedText: { fontSize: '13px', fontWeight: 'bold', color: '#856404' },
  confirmStatusRow: {
    display: 'flex', alignItems: 'center', gap: '8px',
    backgroundColor: 'white', borderRadius: '8px', padding: '10px 12px', flexWrap: 'wrap',
  },
  confirmPartyStatus: { display: 'flex', alignItems: 'center', gap: '6px' },
  confirmPartyLabel: { fontSize: '12px', color: '#666', fontWeight: 'bold' },
  confirmedHoursBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '3px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold' },
  autoConfirmedHoursBadge: { backgroundColor: '#e9ecef', color: '#6c757d', padding: '3px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold' },
  awaitingSmallBadge: { backgroundColor: '#f0f0f0', color: '#999', padding: '3px 10px', borderRadius: '8px', fontSize: '12px' },
  confirmDivider: { color: '#ddd', fontSize: '16px' },
  verifiedBadgeSmall: { backgroundColor: '#d4edda', color: '#155724', padding: '3px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold' },
  disputedBadgeSmall: { backgroundColor: '#fff3cd', color: '#856404', padding: '3px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold' },
  workerClaimedBox: { backgroundColor: '#e8f4fd', borderRadius: '8px', padding: '10px 14px' },
  workerClaimedText: { fontSize: '13px', color: '#2c5282' },
  workerClaimedNote: { fontStyle: 'italic', color: '#4a7aaa' },
  workerClaimedSmall: { fontSize: '12px', color: '#2c5282', marginBottom: '8px' },
  autoVerifyCountdown: { backgroundColor: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '8px', padding: '8px 12px' },
  autoVerifyText: { fontSize: '12px', color: '#276749' },
  hourSelectorBox: { backgroundColor: 'white', borderRadius: '10px', padding: '16px', border: '2px solid #E94560' },
  hourSelectorTitle: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '12px' },
  hourButtons: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' },
  hourBtn: {
    padding: '10px 14px', backgroundColor: '#f0f0f0', color: '#444',
    border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', fontWeight: 'bold', minWidth: '48px', textAlign: 'center', touchAction: 'manipulation',
  },
  hourBtnActive: {
    padding: '10px 14px', backgroundColor: '#E94560', color: 'white',
    border: '1px solid #E94560', borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', fontWeight: 'bold', minWidth: '48px', textAlign: 'center', touchAction: 'manipulation',
  },
  notesInput: {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid #ddd', fontSize: '13px',
    boxSizing: 'border-box', marginBottom: '10px', color: '#333',
  },
  autoVerifyNote: {
    fontSize: '12px', color: '#276749', backgroundColor: '#f0fff4',
    padding: '8px 10px', borderRadius: '6px', marginBottom: '12px', border: '1px solid #9ae6b4',
  },
  hourSelectorButtons: { display: 'flex', gap: '10px' },
  cancelHoursBtn: {
    flex: 1, padding: '12px', backgroundColor: 'transparent', color: '#666',
    border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', touchAction: 'manipulation',
  },
  submitHoursBtn: {
    flex: 2, padding: '12px', backgroundColor: '#28a745', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', touchAction: 'manipulation',
  },
  submitHoursBtnDisabled: {
    flex: 2, padding: '12px', backgroundColor: '#ccc', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'not-allowed', fontSize: '14px', fontWeight: 'bold',
  },
  confirmBtn: {
    padding: '12px 20px', backgroundColor: '#28a745', color: 'white',
    border: 'none', borderRadius: '10px', fontSize: '14px',
    fontWeight: 'bold', cursor: 'pointer', alignSelf: 'flex-start', touchAction: 'manipulation',
  },
  awaitingBox: { backgroundColor: '#fff3cd', borderRadius: '8px', padding: '12px 14px' },
  awaitingText: { fontSize: '13px', color: '#856404', fontWeight: 'bold' },
  disputeDetailBox: {
    backgroundColor: '#fff3cd', borderRadius: '8px', padding: '14px',
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  disputeDetailText: { fontSize: '13px', color: '#856404' },
  disputeNote: { fontSize: '12px', color: '#856404', fontStyle: 'italic' },
  escalateLink: {
    background: 'none', border: 'none', color: '#E94560',
    fontSize: '12px', cursor: 'pointer', textDecoration: 'underline', padding: 0, textAlign: 'left',
  },
  escalationForm: {
    backgroundColor: 'white', borderRadius: '8px', padding: '16px',
    border: '1px solid #ddd', display: 'flex', flexDirection: 'column', gap: '10px',
  },
  escalationTitle: { fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e' },
  escalationWarning: { fontSize: '13px', color: '#856404', backgroundColor: '#fff3cd', padding: '10px', borderRadius: '6px', lineHeight: '1.5' },
  escalationLabel: { fontSize: '13px', fontWeight: 'bold', color: '#555' },
  escalationTextarea: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', boxSizing: 'border-box', resize: 'vertical' },
  escalationUploadBtn: { display: 'inline-block', padding: '8px 14px', backgroundColor: '#1a1a2e', color: 'white', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  escalationButtons: { display: 'flex', gap: '10px' },
  cancelEscalationBtn: { flex: 1, padding: '10px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  submitEscalationBtn: { flex: 2, padding: '10px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },
  submitEscalationBtnDisabled: { flex: 2, padding: '10px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '8px', cursor: 'not-allowed', fontSize: '13px', fontWeight: 'bold' },
  autoVerifiedNotice: { backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '10px 14px', border: '1px solid #ddd' },
  autoVerifiedNoticeText: { fontSize: '12px', color: '#6c757d', fontStyle: 'italic' },
  reportIssueLink: {
    background: 'none', border: 'none', color: '#999',
    fontSize: '11px', cursor: 'pointer', textDecoration: 'underline',
    padding: '4px 0 0 0', textAlign: 'left', marginTop: '4px',
  },
  issueBox: {
    backgroundColor: '#fff8f0', border: '1px solid #ffc107',
    borderRadius: '10px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px',
  },
  issueWarningTitle: { fontSize: '15px', fontWeight: 'bold', color: '#856404' },
  issueWarningText: { fontSize: '13px', color: '#6b5500', lineHeight: '1.6' },
  issueStepTitle: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e' },
  issueReasonList: { display: 'flex', flexDirection: 'column', gap: '6px' },
  issueReason: { padding: '10px 14px', backgroundColor: 'white', color: '#555', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', textAlign: 'left', touchAction: 'manipulation' },
  issueReasonActive: { padding: '10px 14px', backgroundColor: '#fff3cd', color: '#856404', border: '2px solid #ffc107', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', textAlign: 'left', fontWeight: 'bold' },
  issueTextarea: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', boxSizing: 'border-box', resize: 'vertical' },
  issueOutcomeRow: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  issueOutcome: { flex: 1, padding: '14px', backgroundColor: 'white', border: '2px solid #ddd', borderRadius: '10px', cursor: 'pointer', minWidth: '140px', touchAction: 'manipulation' },
  issueOutcomeSelected: { flex: 1, padding: '14px', backgroundColor: '#fff3cd', border: '2px solid #ffc107', borderRadius: '10px', cursor: 'pointer', minWidth: '140px', touchAction: 'manipulation' },
  issueOutcomeTitle: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '6px' },
  issueOutcomeDesc: { fontSize: '12px', color: '#666', lineHeight: '1.5' },
  issueStepButtons: { display: 'flex', gap: '10px' },
  issueCancelBtn: { padding: '10px 16px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', touchAction: 'manipulation' },
  issueContinueBtn: { flex: 1, padding: '10px', backgroundColor: '#856404', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', touchAction: 'manipulation' },
  issueSubmitBtn: { flex: 1, padding: '10px', backgroundColor: '#856404', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', touchAction: 'manipulation' },
  issueSubmitBtnRed: { flex: 1, padding: '10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', touchAction: 'manipulation' },
  issueSubmitBtnDisabled: { flex: 1, padding: '10px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '8px', cursor: 'not-allowed', fontSize: '13px', fontWeight: 'bold' },
  notificationBadge: {
    position: 'absolute', top: '12px', right: '12px',
    backgroundColor: '#E94560', color: 'white',
    borderRadius: '50%', width: '24px', height: '24px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '12px', fontWeight: 'bold',
  },
  activeStatusCard: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '20px', marginBottom: '20px', border: '2px solid #28a745' },
  inactiveStatusCard: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '20px', marginBottom: '20px', border: '2px solid #E94560' },
  activeStatusHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' },
  activeStatusTitle: { fontSize: '18px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  activeStatusSubtitle: { fontSize: '13px', color: '#666' },
  activeStatusBadge: { padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold' },
  editProfileBtn: { padding: '8px 14px', backgroundColor: 'transparent', color: '#E94560', border: '1px solid #E94560', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  inactiveBox: { backgroundColor: '#fff3cd', borderRadius: '8px', padding: '16px' },
  inactiveText: { fontSize: '14px', color: '#856404', marginBottom: '14px' },
  inactiveChecklist: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
  checkItemDone: { fontSize: '13px', color: '#155724', backgroundColor: '#d4edda', padding: '6px 12px', borderRadius: '8px', fontWeight: 'bold' },
  checkItemMissing: { fontSize: '13px', color: '#856404', backgroundColor: '#fff8e1', padding: '6px 12px', borderRadius: '8px', border: '1px dashed #ffc107' },
  checkItemMissingClickable: { fontSize: '13px', color: '#856404', backgroundColor: '#fff8e1', padding: '6px 12px', borderRadius: '8px', border: '1px dashed #ffc107', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  checkItemLink: { fontSize: '12px', color: '#E94560', fontWeight: 'bold', marginLeft: '8px' },
  inactiveActions: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  completeProfileBtn: { padding: '10px 20px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  tradeStatusGrid: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' },
  tradeStatusItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '12px 16px', gap: '12px', flexWrap: 'wrap' },
  tradeStatusLeft: { display: 'flex', flexDirection: 'column', gap: '2px' },
  tradeStatusName: { fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e' },
  tradeStatusLevel: { fontSize: '12px', color: '#666' },
  tradeStatusIndustries: { fontSize: '11px', color: '#999' },
  tradeStatusBadge: { padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 },
  statusNote: { backgroundColor: '#fff3cd', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#856404', marginBottom: '10px' },
  statusNoteWarn: { backgroundColor: '#f8d7da', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#721c24', marginBottom: '10px' },
  certStatusRow: { display: 'flex', alignItems: 'flex-start', gap: '8px', paddingTop: '10px', borderTop: '1px solid #f0f0f0', flexWrap: 'wrap' },
  certStatusLabel: { fontSize: '13px', fontWeight: 'bold', color: '#555', flexShrink: 0 },
  certStatusValue: { fontSize: '12px', color: '#666', lineHeight: '1.6' },
  welcome: { fontSize: '26px', color: '#1a1a2e', marginBottom: '8px' },
  subtitle: { color: '#666', marginBottom: '24px' },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' },
  card: { backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', textAlign: 'center' },
  cardTitle: { fontSize: '17px', color: '#1a1a2e', marginBottom: '10px' },
  cardText: { color: '#666', marginBottom: '16px', lineHeight: '1.5', fontSize: '13px' },
  cardButton: { padding: '10px 20px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation' },
  noTypeContainer: { textAlign: 'center', padding: '40px', color: '#666' },
};

export default Dashboard;